import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts"
import type {
  MonthlyCompletedData,
  ResponseTimeData,
  TeamTaskCountData,
} from "../types/analytics.types"
import { MONTHLY_SERIES_COLORS } from "../constants/analyticsConstants"
import { ChartEmptyOverlay } from "../../../components/ChartEmptyOverlay"

interface ResponseTimeChartProps {
  data?: ResponseTimeData[]
  emptyTitle?: string
  emptyMessage?: string
}

export function ResponseTimeChart({ data, emptyTitle, emptyMessage }: ResponseTimeChartProps): JSX.Element {
  const isEmpty = !data || data.length === 0

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
      <h3 className="mb-4 text-base font-semibold text-gray-900">Response Time by Team</h3>
      {isEmpty ? (
        <ChartEmptyOverlay title={emptyTitle} message={emptyMessage} />
      ) : (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="team" tick={{ fontSize: 12 }} />
              <YAxis unit="m" tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar
                dataKey="minutes"
                fill="#A3C93A"
                radius={[4, 4, 0, 0]}
                animationBegin={0}
                animationDuration={800}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}

interface TasksByTeamChartProps {
  data?: TeamTaskCountData[]
  emptyTitle?: string
  emptyMessage?: string
}

export function TasksByTeamChart({ data, emptyTitle, emptyMessage }: TasksByTeamChartProps): JSX.Element {
  const isEmpty = !data || data.length === 0

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
      <h3 className="mb-4 text-base font-semibold text-gray-900">Tasks by Team</h3>
      {isEmpty ? (
        <ChartEmptyOverlay title={emptyTitle} message={emptyMessage} />
      ) : (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="team" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar
                dataKey="count"
                name="Tasks"
                fill="#A3C93A"
                radius={[4, 4, 0, 0]}
                animationBegin={0}
                animationDuration={800}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}

interface TaskCompletedChartProps {
  data?: MonthlyCompletedData[]
  emptyTitle?: string
  emptyMessage?: string
}

export function TaskCompletedChart({ data, emptyTitle, emptyMessage }: TaskCompletedChartProps): JSX.Element {
  const isEmpty = !data || data.length === 0

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
      <h3 className="mb-4 text-base font-semibold text-gray-900">Tasks Completed per Month</h3>
      {isEmpty ? (
        <ChartEmptyOverlay title={emptyTitle} message={emptyMessage} />
      ) : (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar
                dataKey="completed"
                name="Completed"
                fill={MONTHLY_SERIES_COLORS.completed}
                radius={[4, 4, 0, 0]}
                animationBegin={0}
                animationDuration={800}
              />
              <Bar
                dataKey="closed"
                name="Closed"
                fill={MONTHLY_SERIES_COLORS.closed}
                radius={[4, 4, 0, 0]}
                animationBegin={0}
                animationDuration={800}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}
